import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Instagram, MapPin, Send, MessageCircle } from 'lucide-react';
import { trackPageView } from '../lib/analytics';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { trackPageView('/contacto', 'Contacto'); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Open WhatsApp with the message
    const whatsappMessage = `*Nuevo mensaje de contacto*\n\nNombre: ${formData.name}\nEmail: ${formData.email}\nTeléfono: ${formData.phone}\n\nMensaje:\n${formData.message}`;
    window.open(`https://wa.me/584242925792?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
    
    toast.success('¡Gracias por contactarnos! Te responderemos pronto.');
    setFormData({ name: '', email: '', phone: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="text-gold font-accent text-xl italic mb-4">Estamos para ayudarte</p>
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-6">Contacto</h1>
            <p className="text-white/60 max-w-2xl mx-auto">
              ¿Tienes alguna pregunta sobre nuestros productos o necesitas ayuda con tu pedido? 
              Escríbenos y te responderemos lo antes posible.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-2xl font-serif text-white mb-8">Información de Contacto</h2>
              
              <div className="space-y-6">
                <a 
                  href="https://wa.me/584242925792"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start p-6 bg-[#0A0A0A] border border-white/10 hover:border-gold/50 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#25D366]/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1 group-hover:text-gold transition-colors">WhatsApp</h3>
                    <p className="text-white/60">+58 424 292 5792</p>
                    <p className="text-gold text-sm mt-2">Respuesta inmediata →</p>
                  </div>
                </a>

                <a 
                  href="https://instagram.com/davocadoswimwear"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start p-6 bg-[#0A0A0A] border border-white/10 hover:border-gold/50 transition-colors group"
                >
                  <div className="w-12 h-12 bg-pink-500/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <Instagram className="w-6 h-6 text-pink-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1 group-hover:text-gold transition-colors">Instagram</h3>
                    <p className="text-white/60">@davocadoswimwear</p>
                    <p className="text-gold text-sm mt-2">Síguenos para novedades →</p>
                  </div>
                </a>

                <a 
                  href="mailto:info@davocadoswimwear.com"
                  className="flex items-start p-6 bg-[#0A0A0A] border border-white/10 hover:border-gold/50 transition-colors group"
                >
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1 group-hover:text-gold transition-colors">Email</h3>
                    <p className="text-white/60">info@davocadoswimwear.com</p>
                    <p className="text-white/40 text-sm mt-2">Respondemos en 24 horas</p>
                  </div>
                </a>

                <div className="flex items-start p-6 bg-[#0A0A0A] border border-white/10">
                  <div className="w-12 h-12 bg-avocado/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-avocado" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Ubicación</h3>
                    <p className="text-white/60">Venezuela</p>
                    <p className="text-white/40 text-sm mt-2">Envíos a todo el país</p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-8 p-6 bg-[#0A0A0A] border border-white/10">
                <h3 className="text-white font-medium mb-4">Horario de Atención</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Lunes - Viernes</span>
                    <span className="text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Sábado</span>
                    <span className="text-white">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Domingo</span>
                    <span className="text-white/40">Cerrado</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-2xl font-serif text-white mb-8">Envíanos un Mensaje</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Nombre *</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-gold"
                    placeholder="Tu nombre completo"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-gold"
                    placeholder="tu@email.com"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Teléfono</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-gold"
                    placeholder="+58 XXX XXX XXXX"
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Mensaje *</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-gold resize-none"
                    placeholder="¿En qué podemos ayudarte?"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gold-solid py-4 flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span>Enviando...</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Mensaje
                    </>
                  )}
                </Button>

                <p className="text-white/40 text-sm text-center">
                  Al enviar el mensaje serás redirigido a WhatsApp para una respuesta más rápida.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
